@if (transaction() !== undefined) {
  @if (invoiceDetail(); as invoice) {
    <odin-print-invoice [invoiceDetail]="invoice"></odin-print-invoice>
  } @else {
    <odin-print-receipt [receiptDetail]="createReceipt()"></odin-print-receipt>
  }
}

<div class="p-2 page-height overflow-auto bg-panel">
  @if (transaction !== undefined && !loading) {
    <div class="detail-page mx-auto">

      <!-- Header: back / title / status / actions -->
      <header class="page-head">
        <button class="back-btn main-panel"
                (click)="BackButtonHandle()" data-test="tx-detail-back-button">
          <mat-icon>chevron_left</mat-icon>
        </button>
        <h5 class="page-title my-0">{{ transactionType }} Detail</h5>

        <span class="status-pill" [ngClass]="'pill-' + statusPillKind()" data-test="tx-detail-status-pill">
          <span class="dot"></span>{{ statusPillLabel() }}
        </span>

        <div class="head-actions">
          @if (canPrint()) {
            <button mat-flat-button data-test="Receipt-Options"
                    [matMenuTriggerFor]="receiptMenu"
                    class="act-btn act-primary">
              Receipt <mat-icon iconPositionEnd>arrow_drop_down</mat-icon>
            </button>
            <mat-menu #receiptMenu="matMenu" xPosition="before">
              <button mat-menu-item (click)="printReceipt()" data-test="Receipt-Print">
                <mat-icon>receipt_long</mat-icon>
                <span>Receipt</span>
              </button>
              <button mat-menu-item (click)="openInvoiceDialog()" data-test="Invoice-Print">
                <mat-icon>request_quote</mat-icon>
                <span>Invoice</span>
              </button>
            </mat-menu>
          }

          @if (canReverse()) {
            <button mat-flat-button (click)="startReverseRequest()" data-test="Reverse"
                    class="act-btn act-danger">
              Reverse
            </button>
          }

          @if (canRefund()) {
            <button mat-flat-button [disabled]="transaction().remainingValue === 0"
                    (click)="startRefundRequest()" data-test="Refund"
                    class="act-btn act-danger">
              Refund
            </button>
          }

          @if (canReCharge()) {
            <button mat-flat-button (click)="startRechargeRequest()" data-test="Recharge"
                    class="act-btn act-warning">
              Recharge
            </button>
          }

          @if (canComplete()) {
            <button mat-flat-button (click)="startPreAuthCompleteRequest()" data-test="Complete"
                    class="act-btn act-success">
              Capture
            </button>
          }
        </div>
      </header>

      <!-- Hero: who / card / amount / when -->
      <section class="main-panel rounded-3 hero" aria-label="Transaction summary">
        <div class="hero-main">
          <div class="chan-icon">
            <fa-icon [icon]="transactionIcon(transaction())"
                     [matTooltip]="transactionIconTooltip(transaction())"
                     matTooltipPosition="right" size="lg"></fa-icon>
          </div>
          <div class="hero-who">
            <p class="hero-name my-0" data-test="cardHolderName">
              {{ transaction().cardHolderDetails.name || 'No Name' }}
            </p>
            <p class="hero-card-line spaced-num my-0" data-test="maskedCard">{{ formattedCardNumber(transaction().maskedCard) }}</p>
          </div>
        </div>
        <div class="hero-amount">
          <div class="amount-big">
            <span data-test="amount">{{ transaction().amount | currency: transaction().currencyCountry }}</span>
            <span class="cur" data-test="currency">({{ transaction().currencyCountry }})</span>
          </div>
          <div class="hero-when" data-test="createdOn">
            <span class="upper-time">{{ transaction().createdOn | date: 'dd/MM/yy h:mm a' }}</span>
            · <span data-test="formattedChannel">{{ transaction().formattedChannel }}</span>
          </div>
          @if (heroChips().length > 0) {
            <div class="hero-chips">
              @for (chip of heroChips(); track chip.label) {
                <span class="status-pill sm" [ngClass]="'pill-' + chip.kind">{{ chip.label }}</span>
              }
            </div>
          }
        </div>
      </section>

      <!-- Uncaptured pre-auth banner -->
      @if (isUncapturedPreAuth()) {
        <div class="capture-note" data-test="tx-detail-capture-banner">
          <span class="t">Uncaptured pre-authorisation</span>
          <span class="capture-note-sub">
            Funds are held on the customer's card until captured or reversed.
            Capture once — to take further payments from this card, use Recharge.
          </span>
          @if (canComplete()) {
            <button mat-flat-button (click)="startPreAuthCompleteRequest()"
                    data-test="tx-detail-capture-banner-button"
                    class="act-btn act-success">
              Capture payment
            </button>
          }
        </div>
      }

      <div class="cols">
        <!-- Main column: the story of the transaction -->
        <div class="col-main">

          @if (orderTracking(); as tracking) {
            <odin-order-tracking-card class="main-panel rounded-3 detail-card"
                                      [orderTracking]="tracking"
                                      [updating]="orderStatusUpdating"
                                      (statusSelected)="onOrderStatusSelected($event)">
            </odin-order-tracking-card>
          }

          <odin-tx-event-timeline class="main-panel rounded-3 detail-card"
                                  [events]="timelineEvents()">
          </odin-tx-event-timeline>

          @if (showPaymentReference()) {
            <section class="main-panel rounded-3 detail-card" aria-label="Payment">
              <div class="card-head"><h2>Payment</h2></div>
              <div class="card-body">
                <div class="kv-row">
                  <span class="kv-k">Reference</span>
                  <span class="kv-v" data-test="paymentReference">
                    {{ transaction().paymentReference }}
                    <button class="icon-btn" (click)="copyToClipboard(transaction().paymentReference)"
                            aria-label="Copy payment reference" data-test="tx-detail-payment-reference-copy">
                      <mat-icon>content_copy</mat-icon>
                    </button>
                  </span>
                </div>
              </div>
            </section>
          }

          @if (showMerchantData()) {
            <odin-merchant-data-card class="main-panel rounded-3 detail-card"
                                     [value]="transaction().paymentDetail">
            </odin-merchant-data-card>
          }

          @if (displayBasket(); as basket) {
            <odin-basket-card class="main-panel rounded-3 detail-card"
                              [basket]="basket"
                              [currency]="transaction().currencyCountry"
                              [chargedAmount]="transaction().amount">
            </odin-basket-card>
          }

          <!-- Callbacks: loaded asynchronously (3rd party) -->
          @if (canAccessWebhooks()) {
            <section class="main-panel rounded-3 detail-card" aria-label="Callback attempts">
              <div class="card-head">
                <h2>Callback attempts</h2>
                <div class="flex-fill"></div>

                @if (!callbacksLoading) {
                  <button mat-icon-button (click)="getCallbackDetails()"
                          data-test="Refresh-Callback" class="refresh-btn" aria-label="Refresh">
                    <mat-icon>refresh</mat-icon>
                  </button>
                }
                @if (callbacksLoading) {
                  <div class="cb-spinner d-flex align-items-center justify-content-center">
                    <div class="spinner-grow spinner-grow-sm" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  </div>
                }

                <button mat-flat-button
                        [disabled]="callbackAttempts.length === 0"
                        (click)="startResendCallback()"
                        data-test="tx-detail-callback-resend-button"
                        class="act-btn act-danger">
                  Resend<span class="d-none d-md-inline"> Callback</span>
                </button>
              </div>

              <div class="cb-scroll">
                <table class="cb-table">
                  @if (callbackAttempts && callbackAttempts.length > 0) {
                    <thead>
                    <tr>
                      <th>Attempt ID</th>
                      <th>Duration</th>
                      <th>Status</th>
                      <th>Requested</th>
                      <th class="d-none d-sm-table-cell">Endpoint</th>
                    </tr>
                    </thead>
                  }
                  <tbody>
                    @for (attempt of callbackAttempts; track attempt.id) {
                      <tr (click)="callbackDialogBox(attempt)" class="cb-row"
                          [attr.data-test]="'tx-detail-callback-row'">
                        <td>{{ attempt.id }}</td>
                        <td>{{ attempt.responseDurationMs }}ms</td>
                        <td>
                          <strong [ngClass]="{
                            'text-success': attempt.responseStatusCode >= 200 && attempt.responseStatusCode < 300,
                            'text-danger': attempt.responseStatusCode < 200 || attempt.responseStatusCode >= 300
                          }">{{ attempt.responseStatusCode }}</strong>
                        </td>
                        <td class="upper-time">{{ attempt.timestamp | date: 'dd/MM/yy h:mm:ss a' }}</td>
                        <td class="d-none d-sm-table-cell cb-url">{{ attempt.url }}</td>
                      </tr>
                    }

                    @if (callbacksLoading) {
                      <tr>
                        <td colspan="5" class="text-center text-muted py-3">
                          Fetching callback details for this transaction.
                        </td>
                      </tr>
                    }

                    @if (!callbacksLoading && callbackAttempts && callbackAttempts.length === 0) {
                      <tr>
                        <td colspan="5" class="text-center text-muted py-3">
                          No callback details found for this transaction.
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </section>
          }
        </div>

        <!-- Side column: reference material -->
        <aside class="col-side">

          @if (hasCustomerDetails()) {
            <section class="main-panel rounded-3 detail-card" aria-label="Customer">
              <div class="card-head"><h2>Customer</h2></div>
              <div class="card-body cust">
                @if (transaction().cardHolderDetails.name) {
                  <div class="cust-row">
                    <mat-icon>person</mat-icon>
                    <span class="val cust-name">{{ transaction().cardHolderDetails.name }}</span>
                  </div>
                }
                @if (customerAddress()) {
                  <div class="cust-row">
                    <mat-icon>place</mat-icon>
                    <span class="val cust-addr" data-test="cardHolderAddress">{{ customerAddress() }}</span>
                  </div>
                }
                @if (billingAddress()) {
                  <div class="cust-row" title="Billing address">
                    <mat-icon>receipt_long</mat-icon>
                    <span class="val cust-addr" data-test="billingAddress">{{ billingAddress() }}</span>
                  </div>
                }
                @if (deliveryAddress()) {
                  <div class="cust-row" title="Delivery address">
                    <mat-icon>local_shipping</mat-icon>
                    <span class="val cust-addr" data-test="deliveryAddress">{{ deliveryAddress() }}</span>
                  </div>
                }
                @if (transaction().cardHolderDetails.emailAddress) {
                  <div class="cust-row">
                    <mat-icon>mail</mat-icon>
                    <a class="val" data-test="cardHolderEmail"
                       [href]="'mailto:' + transaction().cardHolderDetails.emailAddress">
                      {{ transaction().cardHolderDetails.emailAddress }}
                    </a>
                  </div>
                }
                @if (transaction().cardHolderDetails.phoneNumber) {
                  <div class="cust-row">
                    <mat-icon>call</mat-icon>
                    <a class="val spaced-num" data-test="cardHolderPhone"
                       [href]="phoneLink(transaction().cardHolderDetails.phoneNumber)">
                      {{ formattedPhoneNumber(transaction().cardHolderDetails.phoneNumber) }}
                    </a>
                  </div>
                }
              </div>
            </section>
          }

          <section class="main-panel rounded-3 detail-card" aria-label="Payment method">
            <div class="card-head"><h2>Payment method</h2></div>
            <div class="card-body">
              <div class="pm">
                <div class="pm-chip">
                  @if (transaction().cardIcon.iconType === 'ICON') {
                    <mat-icon>{{ transaction().cardIcon.value }}</mat-icon>
                  } @else if (transaction().cardIcon) {
                    <img [src]="transaction().cardIcon.value" alt=""/>
                  }
                </div>
                <div>
                  <div class="pm-num spaced-num">{{ formattedCardNumber(transaction().maskedCard) }}</div>
                  <div class="pm-sub">
                    <span data-test="cardType">{{ transaction().cardType }}</span>
                    @if (transaction().expiryDate) {
                      · expires <span data-test="expiryDate">{{ transaction().expiryDate }}</span>
                    }
                  </div>
                </div>
              </div>

              @if (cardInfo(); as info) {
                <div class="pm-details">
                  @if (info.issuer) {
                    <div class="kv-row"><span class="kv-k">Issuer</span>
                      <span class="kv-v" data-test="tx-detail-card-issuer">{{ info.issuer }}</span></div>
                  }
                  @if (info.issuingCountry) {
                    <div class="kv-row"><span class="kv-k">Country</span>
                      <span class="kv-v" data-test="tx-detail-card-country">{{ info.issuingCountry }}</span></div>
                  }
                  @if (info.fundingType || info.product) {
                    <div class="kv-row"><span class="kv-k">Card type</span>
                      <span class="kv-v" data-test="tx-detail-card-product">{{ cardProductLabel(info) }}</span></div>
                  }
                </div>
              }
            </div>
          </section>

          @if (relatedTransactions().length > 0) {
            <section class="main-panel rounded-3 detail-card" aria-label="Related transactions">
              <div class="card-head"><h2>Related transactions</h2></div>
              <div class="card-body related-list">
                @for (related of relatedTransactions(); track related.crossReference) {
                  <button (click)="OpenRelation(related)"
                          [attr.data-test]="'related['+related.crossReference+']'"
                          class="related-item"
                          [ngClass]="isSuccess(related, true) ? 'rel-good' : 'rel-bad'">
                    <span class="rel-icon">
                      <fa-icon [icon]="transactionIcon(related)"
                               [matTooltip]="transactionIconTooltip(related)"
                               matTooltipPosition="right"></fa-icon>
                    </span>
                    <span class="rel-body">
                      <span class="rel-line1">
                        <span class="rel-type">{{ GetTransactionType(related) }}</span>
                        <span class="rel-amt">{{ related.amount | currency: related.currencyCountry }}</span>
                        <span class="rel-when upper-time">{{ related.createdOn | date: 'dd/MM/yy h:mm a' }}</span>
                      </span>
                      <span class="rel-id">{{ related.crossReference }}</span>
                    </span>
                  </button>
                }
              </div>
            </section>
          }

          <section class="main-panel rounded-3 detail-card" aria-label="Technical details">
            <div class="card-head"><h2>Technical</h2></div>
            <div class="card-body">
              <div class="kv-row">
                <span class="kv-k">{{ transactionType }} ID</span>
                <span class="kv-v" data-test="crossRef">
                  {{ transaction().crossReference }}
                  <button class="icon-btn" (click)="copyToClipboard(transaction().crossReference)"
                          aria-label="Copy transaction ID" data-test="tx-detail-cross-reference-copy">
                    <mat-icon>content_copy</mat-icon>
                  </button>
                </span>
              </div>

              @if (!shelper.isNullOrEmpty(transaction().operatorId)) {
                <div class="kv-row">
                  <span class="kv-k">Completed by</span>
                  <span class="kv-v" data-test="operatorName"
                        [attr.data-user-id]="transaction().operatorId">{{ transaction().operatorName }}</span>
                </div>
              }

              @if (ipAddress()) {
                <div class="kv-row">
                  <span class="kv-k">IP address</span>
                  <span class="kv-v" data-test="iPv4Address">{{ ipAddress() }}</span>
                </div>
              }

              @if (userAgent()) {
                <div class="kv-row">
                  <span class="kv-k">Browser</span>
                  <span class="kv-v">
                    <span class="ua-val" [matTooltip]="userAgent()" matTooltipPosition="above"
                          data-test="tx-detail-user-agent">{{ userAgentSummary() }}</span>
                  </span>
                </div>
              }

              @if (terminalId()) {
                <div class="kv-row">
                  <span class="kv-k">Terminal</span>
                  <span class="kv-v" data-test="tx-detail-terminal-id">{{ terminalId() }}</span>
                </div>
              }

              @if (showRefundAmount() && transaction().transactionType !== 'refund') {
                <div class="kv-row">
                  <span class="kv-k">Refunded</span>
                  <span class="kv-v" data-test="amountRefunded">
                    {{ transaction().amountRefunded || 0 | currency: transaction().currencyCountry }}
                  </span>
                </div>
                <div class="kv-row">
                  <span class="kv-k">Remaining</span>
                  <span class="kv-v" data-test="remainingValue">
                    {{ transaction().remainingValue | currency: transaction().currencyCountry }}
                  </span>
                </div>
              }

              @if (!shelper.isNullOrEmpty(transaction().paymentReference) && shelper.isGuid(transaction().paymentReference)) {
                <div class="kv-row">
                  <span class="kv-k">Payment ref</span>
                  <span class="kv-v" data-test="paymentReference">{{ transaction().paymentReference }}</span>
                </div>
              }
            </div>
          </section>
        </aside>
      </div>
    </div>
  } @else {
    <div class="detail-page mx-auto" data-test="tx-detail-skeleton">

      <header class="page-head">
        <span class="skel skel-back"></span>
        <span class="skel skel-title"></span>
        <span class="skel skel-pill"></span>
        <div class="head-actions">
          <span class="skel skel-btn"></span>
          <span class="skel skel-btn"></span>
          <span class="skel skel-btn"></span>
        </div>
      </header>

      <section class="main-panel rounded-3 hero">
        <div class="hero-main">
          <span class="skel skel-icon"></span>
          <div class="hero-who">
            <span class="skel skel-line skel-line-lg"></span>
            <span class="skel skel-line skel-line-sm"></span>
          </div>
        </div>
        <div class="hero-amount">
          <span class="skel skel-line skel-line-md"></span>
          <span class="skel skel-line skel-line-sm"></span>
        </div>
      </section>

      <div class="cols">
        <div class="col-main">
          <section class="main-panel rounded-3 detail-card">
            <div class="card-head"><span class="skel skel-line skel-line-xs"></span></div>
            <div class="card-body">
              @for (row of skeletonRows; track row) {
                <span class="skel skel-line skel-line-row"></span>
              }
            </div>
          </section>
          <section class="main-panel rounded-3 detail-card">
            <div class="card-head"><span class="skel skel-line skel-line-xs"></span></div>
            <div class="card-body">
              @for (row of skeletonRows; track row) {
                <span class="skel skel-line skel-line-row"></span>
              }
            </div>
          </section>
        </div>

        <aside class="col-side">
          <section class="main-panel rounded-3 detail-card">
            <div class="card-head"><span class="skel skel-line skel-line-xs"></span></div>
            <div class="card-body">
              @for (row of skeletonRowsShort; track row) {
                <span class="skel skel-line skel-line-row"></span>
              }
            </div>
          </section>
          <section class="main-panel rounded-3 detail-card">
            <div class="card-head"><span class="skel skel-line skel-line-xs"></span></div>
            <div class="card-body">
              @for (row of skeletonRowsShort; track row) {
                <span class="skel skel-line skel-line-row"></span>
              }
            </div>
          </section>
        </aside>
      </div>
    </div>
  }
</div>
